import {
  Button,
  Header,
  Icons,
  IconSize,
  MobileSlideIn,
  SelectInput,
  SelectOption,
  TextInput,
} from '@pdcfrontendui/components';
import { useIsDualView } from '@pdcfrontendui/hooks';
import { currentLanguage } from '../currentLanguage';

import './OfferViewDetail.scss';
import {
  Person,
  Team,
  TeamDuty,
  TeamShift,
} from '@pdcplan/shared/api/TeamPlan';
import { ShiftInfo } from './ShiftCard';
import { TeamMap } from '../api/model';
import { ChangeEvent, useMemo, useState } from 'react';
import { ExchangeCandidateFilterEnum } from '@pdcplan/shared/enums';
import { useFormatter } from '../hooks/useLang';

const scss = {
  Comp: 'OfferViewDetail',
  content: 'content',
  detailCard: 'detail-card',
  candidateSelect: 'candidate-select',
  candidateSelectExplanation: 'candidate-select-explanation',
  offerButton: 'offer-button',
  note: 'note',
  inputs: 'inputs',
};

type Props = {
  show: boolean;
  shift: TeamShift;
  dutyLines: TeamDuty[];
  selectedGroups: string[];
  teams: TeamMap;
  defaultExchangeCandidateFilter: ExchangeCandidateFilterEnum;
  possibleCandidateFilters: ExchangeCandidateFilterEnum[];
  formerEmployee?: Person;
  employee?: Person;
  onBackClick: () => void;
  onOfferClick: (
    exchangeCandidateFilter: ExchangeCandidateFilterEnum,
    remark: string
  ) => void;
};

const OfferViewDetail = ({
  show,
  shift,
  dutyLines,
  selectedGroups,
  teams,
  defaultExchangeCandidateFilter,
  possibleCandidateFilters,
  employee,
  formerEmployee,
  onBackClick,
  onOfferClick,
}: Props) => {
  const [selectedCandidateFilter, setSelectedCandidateFilter] =
    useState<ExchangeCandidateFilterEnum>(defaultExchangeCandidateFilter);
  const [note, setNote] = useState('');
  const isMobile = !useIsDualView();
  const formatter = useFormatter();

  const title = formatter.weekdayDayMonthYearCap(shift.period.from);

  const teamList: Team[] = useMemo(() => Object.values(teams), [teams]);

  const groupLabels = useMemo(
    () =>
      selectedGroups
        .map((group) => teamList.find((team) => team.id === group)?.label)
        .filter((label) => label !== undefined) as string[],
    [selectedGroups, teamList]
  );

  const selectOptions: SelectOption<ExchangeCandidateFilterEnum>[] =
    possibleCandidateFilters.map((filter) => {
      let label = '';

      switch (filter) {
        case ExchangeCandidateFilterEnum.classic:
          label = currentLanguage.exchangeCandidateFilter_classic;
          break;
        case ExchangeCandidateFilterEnum.callinEsque:
          label = currentLanguage.exchangeCandidateFilter_callinEsque;
          break;
        default:
          label = currentLanguage.exchangeCandidateFilter_noCallin;
          break;
      }

      return {
        key: filter,
        value: filter.toString(),
        label: label,
      };
    });

  return (
    <MobileSlideIn
      isMobile={isMobile}
      className={scss.Comp}
      bottom={50}
      absolute
    >
      {show && (
        <>
          <Header centerize>
            {!isMobile && <Header.Spacer />}
            <Header.Left>
              <Icons.ChevronLeft
                size={IconSize.XSmall}
                onClick={() => {
                  onBackClick();
                }}
              />
            </Header.Left>
            <Header.Title>{title}</Header.Title>
          </Header>
          <div className={scss.content}>
            <div className={scss.detailCard}>
              <ShiftInfo
                shift={shift}
                dutyLines={dutyLines}
                shiftDraft={null}
                formerAssignee={formerEmployee}
                hideStatus={true}
                swapDetails={null}
                offeredTo={groupLabels}
                currentEmployee={employee}
                isAssigningShift
              >
                <div className={scss.inputs}>
                  <TextInput
                    className={scss.note}
                    label={currentLanguage.Note}
                    value={note}
                    onChange={(e) => setNote(e.currentTarget.value)}
                    placeholder={currentLanguage.AddInfo}
                  />
                  <SelectInput<ExchangeCandidateFilterEnum | 'none'>
                    className={scss.candidateSelect}
                    label={currentLanguage.exchangeCandidateFilter}
                    value={ExchangeCandidateFilterEnum[selectedCandidateFilter]}
                    onChange={(e: ChangeEvent<HTMLSelectElement>) => {
                      const candidateFilter: ExchangeCandidateFilterEnum =
                        ExchangeCandidateFilterEnum[
                          e.currentTarget
                            .value as keyof typeof ExchangeCandidateFilterEnum
                        ];
                      setSelectedCandidateFilter(candidateFilter);
                    }}
                    options={selectOptions}
                  />
                  <div className={scss.candidateSelectExplanation}>
                    {currentLanguage.SelectCandidateExplanation}
                  </div>
                  <Button
                    className={scss.offerButton}
                    onClick={() => {
                      onOfferClick(selectedCandidateFilter, note);
                    }}
                  >
                    {currentLanguage.OfferShiftToGroups_1(
                      selectedGroups.length
                    )}
                  </Button>
                </div>
              </ShiftInfo>
            </div>
          </div>
        </>
      )}
    </MobileSlideIn>
  );
};

export default OfferViewDetail;
